import { Grid, Typography, useTheme } from "@mui/material";
import {
  TRADE_HISTORY_TABS,
  TRADE_SIDE,
  TRADE_TYPE,
} from "../constants";
import {
  createSearchParams,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  getOpenOrderList,
  getTradeAmountMax,
  getTradeLast24HList,
} from "store/slices/trade/tradeSlice";

import ConfirmAcceptModal from "../Modals/ConfirmAcceptModal";
import { LoadingButton } from "@mui/lab";
import NumberFormats from "components/NumberFormats";
import { checkIfRulesAreAccepted } from "utils/helpers/checkIfRulesAreAccepted";
import { scrollSection } from "utils/helpers/scrollSection";
import useAlertModal from "hooks/useAlertModal";
import useDispatch from "hooks/useDispatch";
import { useFormContext } from "react-hook-form";
import { useMemo } from "react";
import useNotify from "hooks/useNotify";
import { useState } from "react";
import tradeApi from "store/slices/trade/tradeApi";

function SubmitTrade({
  amountPlaceholderCoin,
  minimumTradeAmount,
  maximumTradeAmount,
}) {
  const theme = useTheme();
  const notify = useNotify();
  const location = useLocation();
  const dispatch = useDispatch();

  const { source, destination } = useParams();
  const alert = useAlertModal();
  const navigate = useNavigate();

  const { handleSubmit, watch, reset } = useFormContext();
  const sideId = watch("sideId");
  const amount = watch("amount");
  const isPrice = watch("isPrice");
  const price = watch("price");
  const basketId = watch("basketId");
  const brokerId = watch("brokerId");

  const [searchParams] = useSearchParams();
  const allSearchParams = Object.fromEntries([...searchParams]);
  const tradeHistoryTab = +searchParams.get("tradeHistoryTab") || 0;
  const pageNumber = searchParams.get("page") || 1;
  const pageSize = searchParams.get("perPage") || 10;

  const [tradeInsertInitial, setTradeInsertInitial] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showConfirmAcceptModal, setConfirmAcceptModal] = useState(false);

  const onSubmit = async (payload) => {
    const body = {
      ...payload,
      price: payload?.price || 0,
    };

    delete body.isPrice;

    try {
      setLoading(true);

      const { data } = await tradeApi.tradeInsertInitial(body)

      updateTradeHistory();
      // reset();
      setTradeInsertInitial(data);
      setConfirmAcceptModal(true);

    } catch (error) {
      checkIfRulesAreAccepted({ error, notify, alert, navigate });
    }

    setLoading(false);
  };

  const updateTradeHistory = () => {
    if (tradeHistoryTab === TRADE_HISTORY_TABS.openOrder) {
      dispatch(getOpenOrderList({ pageSize, pageNumber }));
    } else {
      dispatch(getTradeLast24HList());
    }
  };

  const handleCloseConfirmAcceptModal = () => {
    const isMarketTrade =
      tradeInsertInitial?.data?.typeId === TRADE_TYPE.market.id;

    navigate({
      pathname: location.pathname,
      search: `?${createSearchParams({
        ...allSearchParams,
        tradeHistoryTab: isMarketTrade
          ? TRADE_HISTORY_TABS.log24
          : TRADE_HISTORY_TABS.openOrder,
      })}`,
    });

    setConfirmAcceptModal(false);

    scrollSection("trade_history");

    dispatch(
      getTradeAmountMax({
        basketId,
        sideId,
        coinDestinationId: destination,
        coinSourceId: source,
        brokerId,
      })
    );
  };

  const isConfirmDisabled = useMemo(() => {
    if (isPrice) {
      return !amount || !price;
    } else {
      return !amount;
    }
  }, [amount, isPrice, price]);

  const buttonColor = useMemo(() => {
    if (sideId === TRADE_SIDE.Buy.id) return TRADE_SIDE.Buy.color;
    return TRADE_SIDE.Sell.color;
  }, [sideId]);

  return (
    <>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Typography variant="subtitle1" component="p" color="text.secondary">
            Maximum:{" "}
            <Typography
              variant="h6"
              component="b"
              color="text.main"
              fontWeight={600}
            >
              {" "}
              <NumberFormats value={maximumTradeAmount} />{" "}
            </Typography>
            ( {amountPlaceholderCoin} )
          </Typography>

          <Typography variant="subtitle1" component="p" color="text.secondary">
            Minimum:{" "}
            <Typography
              fontWeight={600}
              variant="h6"
              component="b"
              color="text.main"
            >
              {" "}
              <NumberFormats value={minimumTradeAmount} />{" "}
            </Typography>
            ( {amountPlaceholderCoin} )
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <LoadingButton
            variant="contained"
            loading={loading}
            disabled={isConfirmDisabled}
            fullWidth
            color={buttonColor}
            onClick={handleSubmit(onSubmit)}
          >
            Confirm{" "}
            {sideId === TRADE_SIDE.Buy.id
              ? TRADE_SIDE.Buy.name
              : TRADE_SIDE.Sell.name}
          </LoadingButton>
        </Grid>
      </Grid>

      <ConfirmAcceptModal
        color={theme.palette.success.main}
        variant={"success"}
        open={showConfirmAcceptModal}
        onClose={handleCloseConfirmAcceptModal}
        tradeInsertInitial={tradeInsertInitial}
      />
    </>
  );
}

export default SubmitTrade;
